import s from './BizLoopDiagram.module.scss';

// ── 순환 다이어그램(스타디움 트랙 + 원3) — 환경/안전 가이드 공용. 2026-09-09 사용자. ─────────────
//   출처 = Claude Design 시안 `target SVGs/EVS 사업영역 다이어그램 (단독형).html` 이식(좌표·애니메이션 동일).
//   variant='env'  : 환경 제조업(/igm) — WHAT WE DO. 그린 주조 + 시안. (원본 시안 그대로)
//   variant='safety': 안전 SaaS — HOW IT WORKS. 시안 주조 + 경보 원은 경보색(주황). 내용=대시보드 매뉴얼 기반
//                     (실시간 경보·휴게 알림 / SaaS 대시보드 관제 / 모바일 앱 연동). [[evs-dashboard-saas-for-homepage]]
//   ★색 고정(발명 금지): 네이비 #131720 / 그린 #35c17e / 시안 #00AEEF / 경보 주황 #f47a1f / 본문 #96A3B4.
//   구조: 상단 배지(입력) → 원3 → 하단 배지(출력) 순환. 애니메이션=CSS(flow/orbit/pulse), reduce-motion 정지.
const F = 'Pretendard Variable, Pretendard, sans-serif';
const ORBIT = [s.orbitA, s.orbitB, s.orbitC];
const PULSE = [s.pulseA, s.pulseB, s.pulseC];

type Circle = {
  fill: [string, string];      // radialGradient stops (원 채움)
  ring: string;                // 궤도 호 색
  pulse: string;               // 펄스 링 색
  iconStroke: string;
  icon: JSX.Element;           // translate(cx 154) 기준 라인아이콘
  title: string;
  sub: string;
};
type Cfg = {
  eyebrow: string; h2: [string, string];
  track: [string, string, string];   // 트랙 그라디언트 3색(좌·중·우)
  topPill: { text: string; color: string; textFill: string; w: number };
  botPill: { text: string; color: string; textFill: string; w: number };
  circles: [Circle, Circle, Circle];
};

const CX = [250, 500, 750];

// 환경 아이콘(원본 시안 그대로)
const ENV_ICON = [
  <>
    <circle cx="0" cy="-8" r="12" />
    <path d="M-12-8h24" />
    <path d="M0-20c3.8 3.8 5.7 7.7 5.7 12s-1.9 8.2-5.7 12c-3.8-3.8-5.7-7.7-5.7-12s1.9-8.2 5.7-12z" />
    <path d="M-16.5 3.5-13 12.5a6.5 6.5 0 0 0 6 4h7l-3.5-8.5z" />
    <path d="M16.5 3.5 13 12.5a6.5 6.5 0 0 1-6 4H0l3.5-8.5z" />
    <path d="M-16.5 3.5-20.5 1.5" /><path d="M16.5 3.5 20.5 1.5" />
  </>,
  <>
    <path d="M0-21c-6.8 0-12.3 5.5-12.3 12.3C-12.3-.2 0 12 0 12s12.3-12.2 12.3-20.7C12.3-15.5 6.8-21 0-21z" />
    <path d="M2-16.5-5.4-6h4.6l-1.2 7.8 6.6-9.6H-.4z" />
    <path d="M-7 16.5h14" />
  </>,
  <>
    <circle cx="0" cy="-1" r="10.5" />
    <path d="M-10.5-1h21" />
    <path d="M0-11.5c3.3 3.3 5 6.7 5 10.5s-1.7 7.2-5 10.5c-3.3-3.3-5-6.7-5-10.5s1.7-7.2 5-10.5z" />
    <path d="M15.5-6.5A17 17 0 0 1 8 13.5" /><path d="M11 10.5 8 14l-4-2.5" />
    <path d="M-15.5 4.5A17 17 0 0 1-8-15.5" /><path d="M-11-12.5-8-16l4 2.5" />
  </>,
];

// 안전 아이콘 — 경보(벨) / SaaS 대시보드(모니터+차트) / 모바일 앱(폰)
const SAFE_ICON = [
  <>
    <path d="M0-17a9 9 0 0 0-9 9c0 8-3.5 10-3.5 10h25s-3.5-2-3.5-10a9 9 0 0 0-9-9Z" />
    <path d="M-4 6a4 4 0 0 0 8 0" />
  </>,
  <>
    <rect x="-15" y="-15" width="30" height="21" rx="2.5" />
    <path d="M-5 6v6M-11 12h22" />
    <path d="M-10-3-4-8 1-4 10-12" />
  </>,
  <>
    <rect x="-8.5" y="-17" width="17" height="34" rx="3.5" />
    <path d="M-8.5-11h17M-8.5 11h17" />
    <circle cx="0" cy="14" r="1.3" />
  </>,
];

const CONFIGS: Record<'env' | 'safety', Cfg> = {
  env: {
    eyebrow: 'WHAT WE DO',
    h2: ['실시간 데이터로 환경 변화를', '빠르게 감지하고 관리합니다'],
    track: ['#35c17e', '#35c17e', '#00AEEF'],
    topPill: { text: '실시간 데이터 수집 & 분석', color: '#00AEEF', textFill: '#9BE0F8', w: 296 },
    botPill: { text: '환경 변화를 빠르게 감지 & 관리', color: '#35c17e', textFill: '#A5EBCB', w: 328 },
    circles: [
      { fill: ['#183A2C', '#141A26'], ring: '#35c17e', pulse: '#35c17e', iconStroke: '#7FE3B6', icon: ENV_ICON[0], title: '대기오염 관리', sub: '배출·방지시설 원격 확인' },
      { fill: ['#123C4C', '#141A26'], ring: '#00AEEF', pulse: '#00AEEF', iconStroke: '#7FD9F5', icon: ENV_ICON[1], title: '스마트 에너지 시스템', sub: '전력·가동 데이터 통합' },
      { fill: ['#183A2C', '#141A26'], ring: '#35c17e', pulse: '#35c17e', iconStroke: '#7FE3B6', icon: ENV_ICON[2], title: '환경 모니터링', sub: '환경부 GreenLink 전송' },
    ],
  },
  // [2026-09-09 사용자 확정] 안전=폭염 앰버·레드. 온열질환/폭염 도메인 직결 + 환경(그린)과 확실히 구분.
  //   앰버 #f59e0b 주조 / 경보 원·출력 배지 레드 #e5342b (LCD 관심 주황·주의 빨강 단계색과 일관).
  safety: {
    eyebrow: 'HOW IT WORKS',
    h2: ['현장 체감온도를 실시간으로', '감지하고 즉시 대응합니다'],
    track: ['#f59e0b', '#f59e0b', '#e5342b'],
    topPill: { text: '현장 체감온도계(WBGT) 실시간 측정', color: '#f59e0b', textFill: '#F7D49B', w: 360 },
    botPill: { text: '온열질환 예방 · 법적 의무 준수', color: '#e5342b', textFill: '#F5B4A8', w: 312 },
    circles: [
      { fill: ['#3A1A18', '#141A26'], ring: '#e5342b', pulse: '#e5342b', iconStroke: '#F3A594', icon: SAFE_ICON[0], title: '실시간 경보·휴게 알림', sub: 'SMS·FCM·이메일·카톡' },
      { fill: ['#3A2A12', '#141A26'], ring: '#f59e0b', pulse: '#f59e0b', iconStroke: '#F5C97E', icon: SAFE_ICON[1], title: 'SaaS 대시보드 관제', sub: '체감온도 그래프·조치 기록' },
      { fill: ['#3A2A12', '#141A26'], ring: '#f59e0b', pulse: '#f59e0b', iconStroke: '#F5C97E', icon: SAFE_ICON[2], title: '모바일 앱 연동', sub: '총괄·공정 담당자 확인' },
    ],
  },
};

export default function BizLoopDiagram({ variant = 'env' }: { variant?: 'env' | 'safety' }) {
  const c = CONFIGS[variant];
  const p = `bl-${variant}`; // gradient id 접두(변형 간 충돌 방지)
  return (
    <div className={s.wrap}>
      <div className={s.inner}>
        <div className={s.head}>
          <p className={s.eyebrow}>{c.eyebrow}</p>
          <h2 className={s.h2}>{c.h2[0]}<br />{c.h2[1]}</h2>
        </div>

        <svg className={s.svg} viewBox="0 0 1000 380" role="img"
          aria-label={`${c.topPill.text} → ${c.circles.map((x) => x.title).join(' · ')} → ${c.botPill.text}`}>
          <defs>
            <linearGradient id={`${p}-trk`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor={c.track[0]} stopOpacity=".18" />
              <stop offset=".5" stopColor={c.track[1]} stopOpacity=".6" />
              <stop offset="1" stopColor={c.track[2]} stopOpacity=".22" />
            </linearGradient>
            {c.circles.map((cir, i) => (
              <radialGradient key={i} id={`${p}-g${i}`} cx="38%" cy="30%" r="78%">
                <stop offset="0" stopColor={cir.fill[0]} /><stop offset="1" stopColor={cir.fill[1]} />
              </radialGradient>
            ))}
            {c.circles.map((cir, i) => (
              <linearGradient key={i} id={`${p}-ring${i}`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={cir.ring} /><stop offset=".55" stopColor={cir.ring} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>

          {/* 트랙 + 흐르는 점선 */}
          <rect x="96" y="46" width="808" height="288" rx="144" fill="none" stroke="#1E2531" strokeWidth="1.4" />
          <rect x="96" y="46" width="808" height="288" rx="144" fill="none" stroke={`url(#${p}-trk)`} strokeWidth="1.8"
            strokeDasharray="2 12" strokeLinecap="round" className={s.flow} />

          {/* 상단 배지(입력) — 폭은 variant별(문구 길이) */}
          <rect x={500 - c.topPill.w / 2} y="30" width={c.topPill.w} height="32" rx="16" fill="#131720" />
          <rect x={500 - c.topPill.w / 2} y="30" width={c.topPill.w} height="32" rx="16" fill={c.topPill.color} fillOpacity=".1" stroke={c.topPill.color} strokeOpacity=".45" strokeWidth="1" />
          <text x="500" y="51" textAnchor="middle" fontFamily={F} fontSize="14" fontWeight="700" fill={c.topPill.textFill}>{c.topPill.text}</text>

          {/* 하단 배지(출력) */}
          <rect x={500 - c.botPill.w / 2} y="318" width={c.botPill.w} height="32" rx="16" fill="#131720" />
          <rect x={500 - c.botPill.w / 2} y="318" width={c.botPill.w} height="32" rx="16" fill={c.botPill.color} fillOpacity=".1" stroke={c.botPill.color} strokeOpacity=".45" strokeWidth="1" />
          <text x="500" y="339" textAnchor="middle" fontFamily={F} fontSize="14" fontWeight="700" fill={c.botPill.textFill}>{c.botPill.text}</text>

          {/* 원 3개 */}
          {c.circles.map((cir, i) => (
            <g key={i}>
              <circle cx={CX[i]} cy="190" r="96" fill="none" stroke={cir.pulse} strokeOpacity=".26" strokeWidth="1" className={PULSE[i]} />
              <circle cx={CX[i]} cy="190" r="82" fill={`url(#${p}-g${i})`} stroke="#2A3346" strokeWidth="1.2" />
              <g className={ORBIT[i]}>
                <circle cx={CX[i]} cy="190" r="82" fill="none" stroke={`url(#${p}-ring${i})`} strokeWidth="2" strokeLinecap="round" strokeDasharray="150 366" />
              </g>
              <g transform={`translate(${CX[i]} 154)`} stroke={cir.iconStroke} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                {cir.icon}
              </g>
              <text x={CX[i]} y="212" textAnchor="middle" fontFamily={F} fontSize="16" fontWeight="800" letterSpacing="-.4" fill="#fff">{cir.title}</text>
              <text x={CX[i]} y="233" textAnchor="middle" fontFamily={F} fontSize="11.5" fill="#96A3B4">{cir.sub}</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}
